import React, { useState } from 'react';
import './GoodsInTransit.css';
import axios from "axios";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GoodsInTransit = () => {
  const [formData, setFormData] = useState({
    policyNo: '',
    sumInsuredDeclaration: '',
    Description: '',
    loadingPoint: '',
    dischargePoint: '',
    conveyance: '',
    vehicleReg: '',
    vehicleModel: '',
    ownership: '',
    wayBillUpload: '',
    submissionDate: '',
    customerName: '',
    totalSumInsured: '',
    policyIssueDate: '',
    durationOfCover: '',
    remSumInsured: '',
  });

  const [errors, setErrors] = useState({ policyNo: '' });
  const [isLoading, setIsLoading] = useState(false); 
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const eskaBaseUrl = "https://eskabatch.services.heirslifeassurance.com/api/v1"

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "totalSumInsured" || name === "sumInsuredDeclaration") {
      const numericValue = value.replace(/[^0-9.]/g, ''); 
      setFormData({ ...formData, [name]: numericValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const formatCurrency = (value: string): string => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(Number(value));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
        const fileData = new FormData();
        fileData.append('file', e.target.files[0]);
        try {
            const uploadLink = await axios.post(`${eskaBaseUrl}/general/uploadImg`, fileData);
            const uploaded = uploadLink.data.imageUrl;

            setFormData((prevFormData) => ({ ...prevFormData, wayBillUpload: uploaded }));

            toast.success("File uploaded successfully");
        } catch (error) {
            console.error("Error uploading file:", error);
            toast.error("Failed to upload file");
        }
    }
};

  const getPolicyDetails = async (policyNo: string) => {
    setIsLoading(true); 
    try {
      const response = await axios.get(
        `${eskaBaseUrl}/general/marinedetails/${policyNo}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      // console.log('API Response:', response.data);

      const apiData = response.data;
      const formattedEffectiveDate = formatDateToInput(apiData.effectiveDate);

      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayString = `${yyyy}-${mm}-${dd}`;

      setFormData((prevData) => ({
        ...prevData,
        customerName: apiData.customerName || '',
        remSumInsured: apiData.remSumInsured,
        totalSumInsured: apiData.totalSumInsured,
        policyIssueDate: formattedEffectiveDate,
        durationOfCover: apiData.durationOfCover ? apiData.durationOfCover.toString() : '',
        submissionDate: todayString,
      }));
      if(apiData.remSumInsured){
        toast.info(`Your Initial sum insured was: ${formatCurrency(response.data.totalSumInsured)}`)
      }
      setErrors((prevErrors) => ({ ...prevErrors, policyNo: '' })); 
    } catch (error) {
      console.error('Error fetching policy details:', error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        policyNo: 'Failed to fetch policy details. Please check the policy number.',
      }));
    } finally {
      setIsLoading(false); 
    }
  };

  const formatDateToInput = (dateString: string): string => {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00`;
  };

  const validatePolicyNumber = async () => {
    if (!formData.policyNo.match(/^[A-Za-z0-9-]{6,}$/)) {
        setErrors({ ...errors, policyNo: 'Policy number must be at least 6 alphanumeric characters.' });
        return false;
    }

    setErrors({ ...errors, policyNo: '' });

    await getPolicyDetails(formData.policyNo);

    return true;
  };

  const isFormValid = () => {
    const requiredFields = [
      'policyNo',
      'sumInsuredDeclaration',
      'Description',
      'loadingPoint',
      'dischargePoint',
      'conveyance',
      'wayBillUpload',
      'submissionDate',
      'policyIssueDate',
      'durationOfCover',
      'customerName',
      'totalSumInsured',
      'remSumInsured',
    ];

    if (formData.conveyance === 'Land') {
      requiredFields.push('ownership', 'vehicleReg', 'vehicleModel');
    }

    return requiredFields.every((key) => !!formData[key as keyof typeof formData]);
  };

  const submitForm = async () => {
    setIsLoading(true); 
    const payload = {
      ...formData,
      sumInsuredDeclaration: Number(formData.sumInsuredDeclaration) || 0,
      remSumInsured: Number(formData.remSumInsured) || 0,
      totalSumInsured: Number(formData.totalSumInsured),
    };

    try {
      const response = await axios.post(
        `${eskaBaseUrl}/general/goodstransit`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      toast.success(response.data.message);
      setFormData({
        policyNo: '',
        sumInsuredDeclaration: '',
        Description: '',
        loadingPoint: '',
        dischargePoint: '',
        conveyance: '',
        vehicleReg: '',
        vehicleModel: '',
        ownership: '',
        wayBillUpload: '',
        submissionDate: '',
        customerName: '',
        totalSumInsured: '',
        policyIssueDate: '',
        durationOfCover: '',
        remSumInsured: '',
      });
      GITcert(response.data.data);
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || "Failed to submit form";
      toast.error(apiMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const base64Blob = (base64: string, type = 'application/octet-stream') => {
    const binStr = atob(base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type: type });
  }

  const GITcert = (cert: string) => {
    if (cert) {
      const url = URL.createObjectURL(base64Blob(cert, 'application/pdf'));
      setPdfUrl(url);        
      setShowModal(true);     
    }
  }

  return (
    <>
    <div className="goods-in-transit-form">
      <ToastContainer />
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{isLoading ? "Submitting" : "Loading policy details..."}</p>
        </div>
      )}
      <h1>Goods In Transit Form</h1>
      <form>
        <div className="form-grid">
          <div className="form-group">
              <label htmlFor="policyNo">Policy Number</label>
              <input
                  type="text"
                  id="policyNo"
                  name="policyNo"
                  value={formData.policyNo}
                  onChange={handleChange}
                  onBlur={validatePolicyNumber}
                  required
              />
              {errors.policyNo && <span className="error">{errors.policyNo}</span>}
          </div>

          <div className="form-group">
              <label htmlFor="customerName">Customer Name</label>
              <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  readOnly
              />
          </div>

          <div className="form-group">
              <label htmlFor="totalSumInsured">Total Sum Insured</label>
              <input
                  type="text"
                  id="totalSumInsured"
                  name="totalSumInsured"
                  value={formatCurrency(formData.remSumInsured)}
                  onChange={handleChange}
                  required
                  readOnly
              />
          </div>

          <div className="form-group">
              <label htmlFor="sumInsuredDeclaration">Sum Insured at Declaration</label>
              <input
                  type="text"
                  id="sumInsuredDeclaration"
                  name="sumInsuredDeclaration"
                  value={formData.sumInsuredDeclaration}
                  onChange={handleChange}
                  onBlur={(e) => {
                      const formattedValue = formatCurrency(formData.sumInsuredDeclaration);
                      e.target.value = formattedValue;
                  }}
                  onFocus={(e) => {
                      e.target.value = formData.sumInsuredDeclaration;
                  }}
                  placeholder="Declare Sum Insured"
                  required
              />
          </div>

          <div className="form-group">
              <label htmlFor="Description">Description of Goods</label>
              <textarea
                  id="Description"
                  name="Description"
                  value={formData.Description}
                  onChange={handleChange}
                  placeholder="Enter a description of the goods"
                  required
              />
          </div>

          <div className="form-group">
              <label htmlFor="loadingPoint">Point of Loading (Address)</label>
              <input
                  type="text"
                  id="loadingPoint"
                  name="loadingPoint"
                  value={formData.loadingPoint}
                  onChange={handleChange}
                  placeholder="Enter Loading Address"
                  required
              />
          </div>

          <div className="form-group">
              <label htmlFor="dischargePoint">Point of Discharge (Address)</label>
              <input
                  type="text"
                  id="dischargePoint"
                  name="dischargePoint"
                  value={formData.dischargePoint}
                  onChange={handleChange}
                  placeholder="Enter Discharge Address"
                  required
              />
          </div>

          <div className="form-group">
              <label htmlFor="conveyance">Mode of Conveyance</label>
              <select
                  id="conveyance"
                  name="conveyance"
                  value={formData.conveyance}
                  onChange={handleChange}
                  required
              >
                  <option value="">Select Mode</option>
                  <option value="Land">Land</option>
                  <option value="Sea">Sea</option>
                  <option value="Air">Air</option>
              </select>
          </div>

          {formData.conveyance === 'Land' && (
              <>
                  <div className="form-group">
                      <label htmlFor="ownership">Ownership</label>
                      <input
                          type="text"
                          id="ownership"
                          name="ownership"
                          value={formData.ownership}
                          onChange={handleChange}
                      />
                  </div>
                  <div className="form-group">
                      <label htmlFor="vehicleReg">Vehicle Registration</label>
                      <input
                          type="text"
                          id="vehicleReg"
                          name="vehicleReg"
                          value={formData.vehicleReg}
                          onChange={handleChange}
                      />
                  </div>
                  <div className="form-group">
                      <label htmlFor="vehicleModel">Vehicle Model</label>
                      <input
                          type="text"
                          id="vehicleModel"
                          name="vehicleModel"
                          value={formData.vehicleModel}
                          onChange={handleChange}
                      />
                  </div>
              </>
          )}

          <div className="form-group">
              <label htmlFor="wayBillUpload">Waybill Upload</label>
              <input
                  type="file"
                  id="wayBillUpload"
                  name="wayBillUpload"
                  onChange={handleFileChange}
                  required
              />
          </div>

          <div className="form-group">
              <label htmlFor="submissionDate">Date of Submission</label>
              <input
                  type="date"
                  id="submissionDate"
                  name="submissionDate"
                  value={formData.submissionDate}
                  onChange={handleChange}
                  required
                  readOnly
              />
          </div>

          <div className="form-group">
              <label htmlFor="policyIssueDate">Date and Time of Issue</label>
              <input
                  type="datetime-local"
                  id="policyIssueDate"
                  name="policyIssueDate"
                  value={formData.policyIssueDate}
                  onChange={handleChange}
                  required
                  readOnly
              />
          </div>

          <div className="form-group">
              <label htmlFor="durationOfCover">Duration of Cover</label>
              <input
                  type="text"
                  id="durationOfCover"
                  name="durationOfCover"
                  value={`${formData.durationOfCover} days`}
                  onChange={handleChange}
                  required
                  readOnly
              />
          </div>
        </div>
        <button
          type="submit"
          className="submit-button"
          onClick={(e) => { e.preventDefault(); submitForm(); }}
          disabled={!isFormValid() || isLoading}
        >
          {isLoading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>

    {/* Modal for PDF iframe */}
    {showModal && pdfUrl && (
      <div className="modal-overlay" style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div className="modal-content" style={{
          background: '#fff', padding: 20, borderRadius: 8, maxWidth: '90vw', maxHeight: '90vh', position: 'relative'
        }}>
          <button
            onClick={() => { setShowModal(false); setPdfUrl(null); }}
            style={{
              position: 'absolute', top: 10, right: 10, background: 'transparent', border: '1px solid red', fontSize: 32, cursor: 'pointer', color: 'red', borderRadius: '100%',
            }}
            aria-label="Close"
          >&times;</button>
          <iframe
            style={{ minHeight: '80vh', minWidth: '60vw', border: 'none' }}
            src={pdfUrl}
            title="Goods In Transit Document Frame"
          ></iframe>
        </div>
      </div>
    )}
    </>

    
  );
};

export default GoodsInTransit;